import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Box,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { db, auth } from '../../firebase/config';
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
} from 'firebase/firestore';
import {
  createUserWithEmailAndPassword,
  deleteUser,
  sendPasswordResetEmail,
} from 'firebase/auth';

const AdminManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [establishments, setEstablishments] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    establishmentId: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchAdmins();
    fetchEstablishments();
  }, []);

  const fetchAdmins = async () => {
    try {
      const q = query(collection(db, 'users'), where('role', '==', 'admin'));
      const querySnapshot = await getDocs(q);
      const adminsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAdmins(adminsList);
    } catch (error) {
      setError('Erreur lors du chargement des administrateurs');
    }
  };

  const fetchEstablishments = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'establishments'));
      const establishmentsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setEstablishments(establishmentsList);
    } catch (error) {
      setError('Erreur lors du chargement des établissements');
    }
  };

  const handleOpenDialog = (admin = null) => {
    if (admin) {
      setSelectedAdmin(admin);
      setFormData({
        nom: admin.nom,
        prenom: admin.prenom,
        email: admin.email,
        telephone: admin.telephone,
        establishmentId: admin.establishmentId,
      });
    } else {
      setSelectedAdmin(null);
      setFormData({
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        establishmentId: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedAdmin(null);
    setError('');
  };

  const handleSubmit = async () => {
    try {
      if (selectedAdmin) {
        // Mise à jour d'un admin existant
        await updateDoc(doc(db, 'users', selectedAdmin.id), {
          ...formData,
          updatedAt: new Date(),
        });
        setSuccess('Administrateur mis à jour avec succès');
      } else {
        // Création d'un nouvel admin
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          formData.email,
          'password123' // Mot de passe temporaire
        );

        await addDoc(collection(db, 'users'), {
          uid: userCredential.user.uid,
          ...formData,
          role: 'admin',
          createdAt: new Date(),
          updatedAt: new Date(),
        });

        // Envoi d'un email de réinitialisation du mot de passe
        await sendPasswordResetEmail(auth, formData.email);
        
        setSuccess('Administrateur créé avec succès. Un email de réinitialisation du mot de passe a été envoyé.');
      }
      handleCloseDialog();
      fetchAdmins();
    } catch (error) {
      setError('Erreur lors de l\'enregistrement: ' + error.message);
    }
  };

  const handleDelete = async (admin) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet administrateur ?')) {
      try {
        await deleteDoc(doc(db, 'users', admin.id));
        // Si possible, supprimer également le compte auth
        if (admin.uid) {
          try {
            await deleteUser(admin.uid);
          } catch (authError) {
            console.error('Erreur lors de la suppression du compte auth:', authError);
          }
        }
        setSuccess('Administrateur supprimé avec succès');
        fetchAdmins();
      } catch (error) {
        setError('Erreur lors de la suppression');
      }
    }
  };

  const getEstablishmentName = (id) => {
    const establishment = establishments.find(e => e.id === id);
    return establishment ? establishment.nom : 'Non assigné';
  };

  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          onClick={() => handleOpenDialog()}
        >
          Ajouter un administrateur
        </Button>
      </Box>

      <Grid container spacing={3}>
        {admins.map((admin) => (
          <Grid item xs={12} md={6} lg={4} key={admin.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ mr: 2 }}>
                    {admin.prenom?.[0]}{admin.nom?.[0]}
                  </Avatar>
                  <Typography variant="h6">
                    {admin.prenom} {admin.nom}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                >
                  <EmailIcon sx={{ mr: 1, fontSize: 20 }} />
                  {admin.email}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Établissement: {getEstablishmentName(admin.establishmentId)}
                </Typography>
              </CardContent>
              <CardActions>
                <IconButton
                  size="small"
                  onClick={() => handleOpenDialog(admin)}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDelete(admin)}
                >
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedAdmin ? 'Modifier l\'administrateur' : 'Ajouter un administrateur'}
        </DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Prénom"
            fullWidth
            value={formData.prenom}
            onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Nom"
            fullWidth
            value={formData.nom}
            onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Email"
            fullWidth
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            disabled={selectedAdmin}
          />
          <TextField
            margin="dense"
            label="Téléphone"
            fullWidth
            value={formData.telephone}
            onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
          />
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Établissement</InputLabel>
            <Select
              value={formData.establishmentId}
              label="Établissement"
              onChange={(e) => setFormData({ ...formData, establishmentId: e.target.value })}
            >
              {establishments.map((establishment) => (
                <MenuItem key={establishment.id} value={establishment.id}>
                  {establishment.nom}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedAdmin ? 'Modifier' : 'Ajouter'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdminManagement;
